import { useEffect, useState } from "react";
import { ChevronDown, HelpCircle, TriangleAlert } from "lucide-react";

import { cn } from "@/lib/utils";
import type { Assistant, ToolKind } from "../../api";

/**
 * What each tool actually does, where it runs, and how to prove it works.
 *
 * Written from the runtime, not from intent: `channels` mirrors what
 * apps/assistants/tools.py will really offer on each path, so a tenant can
 * never be told a tool works somewhere it is silently withheld.
 */

type Channel = "telnyx" | "twilio" | "talk";

type Help = {
  /** One or two sentences: what happens on a call. */
  what: string;
  /** Where it genuinely runs. Anything absent is withheld from the model. */
  channels: Channel[];
  /** Caveats per channel, shown next to the badge. */
  channelNote?: Partial<Record<Channel, string>>;
  /** Steps that prove it works. */
  test: string[];
  /** Symptom → cause, for when it doesn't. */
  gotchas: string[];
};

const CHANNEL_LABEL: Record<Channel, string> = {
  telnyx: "Telnyx calls",
  twilio: "Twilio calls",
  talk: "Talk (browser)",
};

export const TOOL_HELP: Record<ToolKind, Help> = {
  api_request: {
    what:
      "Mid-conversation, the assistant calls your endpoint and uses the answer in " +
      "its reply. We POST {\"function\": \"<name>\", \"arguments\": {…}} as JSON; " +
      "reply with JSON and the assistant reads it back to the caller.",
    channels: ["telnyx", "twilio", "talk"],
    test: [
      "Open webhook.site and copy your unique URL.",
      "On that page click Edit and set the response to Content type application/json with a body like {\"status\": \"shipped\", \"eta\": \"Thursday\"} — otherwise it replies with plain text and the assistant has nothing useful to say.",
      "Paste the URL above, describe when to use the tool, and save.",
      "Press Talk and ask the question your description covers.",
      "Check three things line up: the assistant says the right thing, webhook.site shows the request, and Recent runs on the tool card shows OK 200.",
    ],
    gotchas: [
      "No run recorded at all: the assistant never called the tool. Sharpen “When to use it” — that text is the only thing it decides from.",
      "Blocked: the URL resolved to a private or internal address. Endpoints must be reachable on the public internet.",
      "Timed out: your endpoint took longer than the timeout. Raise it, or answer faster — the caller is waiting in silence.",
      "A non-JSON reply still works; the assistant just receives it as plain text.",
    ],
  },
  transfer_call: {
    what:
      "Hands the live caller to a human. The assistant says its line, the " +
      "destination is texted a short summary of the conversation so far, then " +
      "the call is transferred and the assistant stops.",
    channels: ["telnyx", "twilio"],
    test: [
      "Set the destination to a phone you can answer, in E.164 form (+14155551234).",
      "Call the assistant's number and ask for a human, a manager or a supervisor.",
      "Expect to hear the transfer line, then for your phone to ring — and to receive a text summarising the call just before it does.",
    ],
    gotchas: [
      "Not offered in Talk: a browser session has no call leg to hand over, so the assistant says so rather than pretending.",
      "The context text goes out on the same carrier as the call, from a number in this workspace. Without one the transfer still happens; only the text is skipped.",
      "The human sees the caller's own number, not the business number, so they know who is on the line.",
      "The assistant stops talking once the handoff starts. If the carrier refuses the transfer, it apologises and stays on the line.",
      "The call log records the part the assistant handled — the minutes you spend talking to the caller afterwards aren't assistant minutes.",
    ],
  },
  end_call: {
    what:
      "Lets the assistant hang up once the conversation is genuinely finished. " +
      "Your closing line is spoken and allowed to finish playing before the " +
      "line drops, so the caller never hears it cut off.",
    channels: ["telnyx", "twilio", "talk"],
    channelNote: {
      talk: "ends the browser session",
    },
    test: [
      "Call the assistant's number, complete a short exchange, then say goodbye and stop talking.",
      "Expect the closing line in full, then the call to end on its own — not to sit open in silence.",
    ],
    gotchas: [
      "Leave the closing line blank and the assistant says its own goodbye. Fill it in and that exact line is spoken last, after whatever it already said.",
      "Hanging up too eagerly is a description problem. Say plainly that it should only end after the caller has said goodbye or has nothing further.",
      "If the caller hung up first, nothing happens — the leg is already gone.",
      "The call log records the call as completed the moment the leg drops, the same as a caller hanging up.",
    ],
  },
  send_text: {
    what:
      "Texts a link, address or reference to the caller while they are still " +
      "on the line. The assistant writes the message; you choose who receives it.",
    channels: ["telnyx", "twilio", "talk"],
    channelNote: {
      talk: "fixed number only",
    },
    test: [
      "Make sure this workspace has an SMS-capable number on the same carrier as the call — many numbers are voice-only, Australian local numbers especially.",
      "Set it to text the caller's own number.",
      "Call from a phone that can receive SMS and ask the assistant to text you the address.",
      "Expect the text within a few seconds, and the assistant to say it's on its way.",
    ],
    gotchas: [
      "The text goes out on the same carrier as the call, from the number it came in on where that number can text. A workspace with no SMS-capable number on that carrier can't send, and the assistant says so rather than pretending.",
      "Voice-capable is not SMS-capable. If no number can text, the carrier rejects the send and its reason is what the assistant reports.",
      "Some countries reject unregistered alphanumeric sender IDs. We retry from the numeric number automatically, but a route that has never been registered will still fail.",
      "In Talk there is no caller to text, so only a fixed number works.",
    ],
  },
  calcom: {
    what:
      "Gives the assistant this workspace's Cal.com connection: checking " +
      "availability, booking, rescheduling and cancelling appointments.",
    channels: ["telnyx", "twilio", "talk"],
    test: [
      "Connect Cal.com under Settings → Integrations first.",
      "Press Talk and ask what's free on a given day, then ask to book one of the slots.",
      "Confirm the booking appears in Cal.com itself.",
    ],
    gotchas: [
      "Without a live connection the assistant isn't offered these functions at all — so it can't promise a booking it has no way to make. Connect it first, or nothing will happen.",
      "The connection belongs to the workspace, not to this assistant: every assistant here books into the same Cal.com account.",
    ],
  },
  odoo: {
    what:
      "Gives the assistant this workspace's Odoo connection: looking up " +
      "contacts and creating leads, calendar events and invoices.",
    channels: ["telnyx", "twilio", "talk"],
    test: [
      "Connect Odoo under Settings → Integrations first.",
      "Press Talk and give the assistant details it should capture as a lead.",
      "Confirm the record appears in Odoo.",
    ],
    gotchas: [
      "Without a live connection the assistant isn't offered these functions at all.",
      "The connection belongs to the workspace, not to this assistant.",
    ],
  },
  press_digit: {
    what:
      "Lets the assistant press keypad keys on a phone call when an automated " +
      "system asks for it — \"press 1 to connect\", or a menu. It decides what " +
      "to press from what it hears and from this tool's description.",
    channels: ["telnyx", "twilio"],
    test: [
      "Write in the description exactly when and what to press, e.g. \"If you hear 'press 1 to connect', press 1.\"",
      "Place an outbound call to a number that answers with such a prompt.",
      "Expect a short pause after the prompt, the key tone, then the assistant's greeting once a person picks up.",
    ],
    gotchas: [
      "Not offered in Talk or on WhatsApp: there is no keypad on those lines.",
      "Pressing before the prompt has finished? Raise the wait before pressing. Some systems ignore keys pressed while they are still talking.",
      "Pressing when it shouldn't? Make the description stricter about which prompts to answer.",
      "On Telnyx the key is sent as a carrier keypad signal, falling back to tones in the audio; on Twilio it is always tones in the audio. Nearly every system accepts either.",
    ],
  },
};

/** Channels this assistant actually answers on, from its attached numbers. */
function assistantChannels(assistant?: Assistant): Set<Channel> {
  const channels = new Set<Channel>(["talk"]); // Talk is always available
  for (const n of assistant?.attached_numbers ?? []) {
    if (n.channel === "voice" && (n.provider === "twilio" || n.provider === "telnyx")) {
      channels.add(n.provider);
    }
  }
  return channels;
}

/** Whether this tool would do anything on the phone numbers the assistant
 * actually answers on. False means it's addable but Talk-only here — worth
 * saying before someone picks it, not after. */
export function runsOnAttachedNumbers(kind: ToolKind, assistant?: Assistant): boolean {
  const phoneNumbers = (assistant?.attached_numbers ?? []).filter(
    (n) => n.channel === "voice",
  );
  if (phoneNumbers.length === 0) return true; // nothing to contradict yet
  const available = assistantChannels(assistant);
  return (TOOL_HELP[kind]?.channels ?? []).some(
    (c) => c !== "talk" && available.has(c),
  );
}

/** Tool kinds whose help you've already had open once.
 *
 * The panel earns its space the first time you meet a tool and is noise
 * every time after, so it opens itself once per kind and then stays shut —
 * still one click away when you want it. Per browser, like any other
 * reading habit; forgetting it just means one more explanation. */
const HELP_SEEN_KEY = "assistants:tool-help-seen";

function readSeen(): string[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(HELP_SEEN_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return []; // storage disabled, or someone left junk under the key
  }
}

function hasSeen(kind: ToolKind): boolean {
  return readSeen().includes(kind);
}

function markSeen(kind: ToolKind) {
  try {
    const seen = readSeen();
    if (!seen.includes(kind)) {
      localStorage.setItem(HELP_SEEN_KEY, JSON.stringify([...seen, kind]));
    }
  } catch {
    /* not worth failing the drawer over */
  }
}

/** Help for one tool kind, shown where it's configured.
 *
 * `defaultOpen` is the caller's opinion (adding a tool, not editing one);
 * it only wins the first time this kind of tool is met. */
export default function ToolHelp({
  kind,
  assistant,
  defaultOpen,
}: {
  kind: ToolKind;
  assistant?: Assistant;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(() => defaultOpen && !hasSeen(kind));

  // Reading it once is what counts, however it came to be open.
  useEffect(() => {
    if (open) markSeen(kind);
  }, [open, kind]);

  const help = TOOL_HELP[kind];
  if (!help) return null;

  // The warning that matters most: this tool can't run on the number this
  // assistant actually answers on, so testing it would be a dead end.
  const available = assistantChannels(assistant);
  const usableHere = help.channels.filter((c) => available.has(c));
  const phoneNumbers = (assistant?.attached_numbers ?? []).filter(
    (n) => n.channel === "voice",
  );
  const deadOnItsNumbers =
    phoneNumbers.length > 0 &&
    !help.channels.some((c) => c !== "talk" && available.has(c));

  return (
    <div className="rounded-lg border bg-muted/40">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs font-medium"
      >
        <HelpCircle className="size-3.5 text-muted-foreground" />
        How this works
        <ChevronDown
          className={cn(
            "ml-auto size-3.5 text-muted-foreground transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div className="space-y-3 border-t px-3 py-3 text-xs text-muted-foreground">
          <p>{help.what}</p>

          <div>
            <div className="mb-1.5 font-medium text-foreground">Where it runs</div>
            <div className="flex flex-wrap gap-1.5">
              {help.channels.map((c) => (
                <span
                  key={c}
                  className={cn(
                    "rounded-full border px-2 py-0.5",
                    usableHere.includes(c)
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                      : "opacity-60",
                  )}
                >
                  {CHANNEL_LABEL[c]}
                  {help.channelNote?.[c] ? ` — ${help.channelNote[c]}` : ""}
                </span>
              ))}
            </div>
            <p className="mt-1.5">
              Anywhere not listed, the assistant is never offered this tool — so it
              can't try something the call has no way to do. WhatsApp runs no tools
              at all yet.
            </p>
          </div>

          {deadOnItsNumbers && (
            <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-amber-700 dark:text-amber-400">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
              <span>
                This assistant answers on{" "}
                {phoneNumbers.map((n) => n.provider).join(", ")}, where this tool
                doesn't run. You can still add it and try it in Talk, but it will
                do nothing on those calls.
              </span>
            </div>
          )}

          <div>
            <div className="mb-1 font-medium text-foreground">How to test it</div>
            <ol className="list-decimal space-y-1 pl-4">
              {help.test.map((step, i) => (
                <li key={i}>{step}</li>
              ))}
            </ol>
          </div>

          <div>
            <div className="mb-1 font-medium text-foreground">If it doesn't work</div>
            <ul className="list-disc space-y-1 pl-4">
              {help.gotchas.map((g, i) => (
                <li key={i}>{g}</li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
}
